/**
 * CollectionReconReportCard.jsx — Phase 30L MVP
 *
 * A self-contained card providing the "Collection & Reconciliation" report
 * download flow (Excel / CSV). It reads the tenant feature flag from
 * `/school/me` (`collection_recon_report_enabled`) — if OFF, the card renders
 * nothing so the feature is entirely invisible to schools that haven't opted in.
 *
 * RBAC:
 *   - school_admin  → sees Collector filter (kept `null` for now — future
 *                     multi-cashier UI will populate a dropdown).
 *   - cashier       → Collector filter hidden; server auto-scopes to self.
 *
 * Zero effect on any other endpoint or KPI.
 */
import React, { useEffect, useMemo, useState } from "react";
import { DownloadSimple, WarningCircle, Receipt, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

import api, { API_BASE, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function daysBetween(fromStr, toStr) {
  if (!fromStr || !toStr) return 0;
  const a = new Date(fromStr);
  const b = new Date(toStr);
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

export default function CollectionReconReportCard() {
  const [flagEnabled, setFlagEnabled] = useState(null); // null=loading, false=off, true=on

  // Filters
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [classId, setClassId] = useState("__all__");
  const [mode, setMode] = useState("__all__");
  const [format, setFormat] = useState("xlsx"); // "xlsx" | "csv"

  const [classes, setClasses] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const rangeDays = useMemo(() => daysBetween(dateFrom, dateTo), [dateFrom, dateTo]);
  const showLargeRangeWarning = rangeDays > 90;
  const rangeInvalid =
    !dateFrom || !dateTo || new Date(dateFrom) > new Date(dateTo);

  // ------------------------------ Bootstrap ------------------------------- //
  useEffect(() => {
    (async () => {
      try {
        const [me, cs] = await Promise.all([
          api.get("/school/me"),
          api.get("/school/classes").catch(() => ({ data: [] })),
        ]);
        setFlagEnabled(!!me.data?.collection_recon_report_enabled);
        setClasses(cs.data || []);
      } catch (e) {
        setFlagEnabled(false);
      }
    })();
  }, []);

  // Feature flag OFF → render nothing (invisible to tenants that haven't opted in).
  if (!flagEnabled) return null;

  const onDownload = async () => {
    if (rangeInvalid) {
      toast.error("Please select a valid date range.");
      return;
    }
    setDownloading(true);
    try {
      const usp = new URLSearchParams();
      usp.set("date_from", dateFrom);
      usp.set("date_to", dateTo);
      if (classId !== "__all__") usp.set("class_ids", classId);
      if (mode !== "__all__") usp.set("payment_modes", mode);

      const url = `${API_BASE}/school/reports/collection-recon.${format}?${usp.toString()}`;
      const token = tokenStore.get();
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 404) {
        // Feature turned off server-side after page load.
        toast.error("This report is not enabled for your school.");
        setFlagEnabled(false);
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const blob = await res.blob();

      // Empty-result guard using the server-provided receipt count.
      const receiptCount = parseInt(res.headers.get("X-Receipt-Count") || "0", 10);
      if (!receiptCount) {
        toast.warning("No fee collections were found for the selected filters.");
        return;
      }

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const name = `collection-recon-${stamp}.${format}`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="border border-slate-200 bg-white rounded-lg p-5 print:hidden"
      data-testid="collection-recon-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Accounts
          </div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900 font-heading mt-0.5 flex items-center gap-2">
            <Receipt size={18} weight="duotone" />
            Collection &amp; Reconciliation Report
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Download a period-scoped receipt register (Cash / UPI / Bank
            breakdown) for accounts reconciliation. Voided receipts are
            excluded.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
        <div>
          <Label className="text-xs">Date From</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 h-9"
            data-testid="recon-date-from"
          />
        </div>
        <div>
          <Label className="text-xs">Date To</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 h-9"
            data-testid="recon-date-to"
          />
        </div>
        <div>
          <Label className="text-xs">Class (optional)</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="mt-1 h-9" data-testid="recon-class-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {classes.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.id}
                  data-testid={`recon-class-opt-${c.id}`}
                >
                  {c.name}
                  {c.section ? ` - ${c.section}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Payment Mode (optional)</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="mt-1 h-9" data-testid="recon-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All modes</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Format</Label>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger className="mt-1 h-9" data-testid="recon-format-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Button
            className="mt-1 h-9 w-full gap-1.5"
            onClick={onDownload}
            disabled={downloading || rangeInvalid}
            data-testid="recon-download-btn"
          >
            {downloading ? (
              <>
                <Spinner /> Generating…
              </>
            ) : (
              <>
                <DownloadSimple size={16} /> Download
              </>
            )}
          </Button>
        </div>
      </div>

      {rangeInvalid && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-1.5"
          data-testid="recon-range-invalid"
        >
          <WarningCircle size={14} className="mt-0.5 shrink-0" />
          <span>Please select a valid date range (Date From must be on/before Date To).</span>
        </div>
      )}

      {showLargeRangeWarning && !rangeInvalid && (
        <div
          className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded flex items-start gap-1.5"
          data-testid="recon-large-range-warning"
        >
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>
            Large date range selected ({rangeDays} days). Report generation may
            take a little longer.
          </span>
        </div>
      )}
    </div>
  );
}


// --- helpers --------------------------------------------------------------- //

function Spinner() {
  return (
    <span
      className="inline-block h-3.5 w-3.5 border-2 border-white/50 border-t-white rounded-full animate-spin"
      aria-hidden
    />
  );
}

// Feature-flag probe consumed by the sidebar to conditionally render the
// Cashier-only shortcut link.
export function useReconReportEnabled() {
  const [enabled, setEnabled] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    if (!user || !["school_admin", "cashier"].includes(user.role)) {
      setEnabled(false); return;
    }
    api.get("/school/me")
      .then((res) => setEnabled(!!res.data?.collection_recon_report_enabled))
      .catch(() => setEnabled(false));
  }, [user]);
  return enabled;
}
